import { ENV } from "@phc/common"
import {
  browserTracingIntegration,
  captureConsoleIntegration,
  captureMessage,
  init,
  replayIntegration,
} from "@sentry/react"
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk"
import { createRoot } from "react-dom/client"
import {
  RouterProvider,
  createBrowserRouter,
  type RouteObject,
} from "react-router"
import { AppProviders } from "./AppProviders"
import { RouteConfig } from "./RouteConfig"
import { AuthWrapper } from "./utils/auth"
import { ENVIRONMENTS, SeverityLevel } from "./utils/constants"

if (import.meta.env.PROD) {
  init({
    dsn: import.meta.env.SENTRY_DSN,
    environment: ENVIRONMENTS[ENV],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      browserTracingIntegration(),
      replayIntegration(),
      captureConsoleIntegration({
        levels: ["error"],
      }),
    ],

    tracesSampleRate: 1.0,
  })
}

const container = document.getElementById("root")
if (!container) {
  captureMessage("No root element found", SeverityLevel.Fatal)
  throw new Error("No root element found")
}

function routeConfig(): RouteObject[] {
  return [
    {
      element: <AuthWrapper />,
      children: [
        {
          element: <AppProviders />,
          children: RouteConfig,
        },
      ],
    },
  ]
}
const launchDarklyId = import.meta.env.LAUNCHDARKLY_CLIENT_ID

void (async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: launchDarklyId,
    options: {
      bootstrap: "localStorage",
    },
  })

  createRoot(container).render(
    <LDProvider>
      <RouterProvider router={createBrowserRouter(routeConfig())} />
    </LDProvider>
  )
})()
